import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Upload, Trash2, Users, Download, Edit, Send, FolderInput, FileDown, CheckSquare, Square } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Recipients = () => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isBulkGroupDialogOpen, setIsBulkGroupDialogOpen] = useState(false);
  const [isBulkEmailDialogOpen, setIsBulkEmailDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [uploadGroup, setUploadGroup] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'unsubscribed', 'all'
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewGroup, setPreviewGroup] = useState('');
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [bulkGroup, setBulkGroup] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    group: ''
  });
  const [editFormData, setEditFormData] = useState({
    email: '',
    name: '',
    group: ''
  });

  useEffect(() => {
    fetchRecipients();
  }, []);

  const fetchRecipients = async () => {
    try {
      const response = await axios.get(`${API}/recipients`);
      setRecipients(response.data);
    } catch (error) {
      toast.error('Failed to fetch recipients');
    } finally {
      setLoading(false);
    }
  };

  const uniqueGroups = [...new Set(recipients.filter(r => r.group).map(r => r.group))];
  
  // Filter by group
  let filteredRecipients = selectedGroup === 'all' 
    ? recipients 
    : recipients.filter(r => r.group === selectedGroup);
  
  // Filter by status
  if (statusFilter === 'active') {
    filteredRecipients = filteredRecipients.filter(r => !r.unsubscribed);
  } else if (statusFilter === 'unsubscribed') {
    filteredRecipients = filteredRecipients.filter(r => r.unsubscribed);
  }
  
  // Statistics
  const totalRecipients = recipients.length;
  const activeRecipients = recipients.filter(r => !r.unsubscribed).length;
  const unsubscribedRecipients = recipients.filter(r => r.unsubscribed).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/recipients`, formData);
      toast.success('Recipient added successfully');
      setIsDialogOpen(false);
      setFormData({ email: '', name: '', group: '' });
      fetchRecipients();
    } catch (error) {
      toast.error('Failed to add recipient');
    }
  };

  const handleEdit = (recipient) => {
    setEditingRecipient(recipient);
    setEditFormData({
      email: recipient.email,
      name: recipient.name || '',
      group: recipient.group || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/recipients/${editingRecipient.id}`, editFormData);
      toast.success('Alıcı bilgileri güncellendi');
      setIsEditDialogOpen(false);
      setEditingRecipient(null);
      fetchRecipients();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Güncelleme başarısız oldu');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu alıcıyı silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/recipients/${id}`);
      toast.success('Alıcı silindi');
      fetchRecipients();
    } catch (error) {
      toast.error('Silme işlemi başarısız oldu');
    }
  };

  // Bulk operations
  const toggleSelectAll = () => {
    if (selectedRecipients.length === filteredRecipients.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(filteredRecipients.map(r => r.id));
    }
  };

  const toggleSelectRecipient = (id) => {
    if (selectedRecipients.includes(id)) {
      setSelectedRecipients(selectedRecipients.filter(rid => rid !== id));
    } else {
      setSelectedRecipients([...selectedRecipients, id]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRecipients.length === 0) {
      toast.error('Lütfen en az bir alıcı seçin');
      return;
    }
    if (!window.confirm(`${selectedRecipients.length} alıcıyı silmek istediğinizden emin misiniz?`)) return;
    
    try {
      await axios.post(`${API}/recipients/bulk-delete`, selectedRecipients);
      toast.success(`${selectedRecipients.length} alıcı silindi`);
      setSelectedRecipients([]);
      fetchRecipients();
    } catch (error) {
      toast.error('Toplu silme başarısız oldu');
    }
  };

  const handleBulkGroupChange = async () => {
    if (selectedRecipients.length === 0) {
      toast.error('Lütfen en az bir alıcı seçin');
      return;
    }
    if (!bulkGroup.trim()) {
      toast.error('Lütfen bir grup adı girin');
      return;
    }

    try {
      // Update each recipient's group
      await Promise.all(
        selectedRecipients.map(id => 
          axios.put(`${API}/recipients/${id}`, { group: bulkGroup })
        )
      );
      toast.success(`${selectedRecipients.length} alıcının grubu güncellendi`);
      setIsBulkGroupDialogOpen(false);
      setBulkGroup('');
      setSelectedRecipients([]);
      fetchRecipients();
    } catch (error) {
      toast.error('Grup güncelleme başarısız oldu');
    }
  };

  const handleExportCSV = () => {
    const csvData = filteredRecipients.map(r => ({
      'İsim Soyisim': r.name || '',
      'Email': r.email,
      'Grup': r.group || '',
      'Durum': r.unsubscribed ? 'Abonelikten Çıkmış' : 'Aktif',
      'Eklenme Tarihi': new Date(r.created_at).toLocaleDateString('tr-TR')
    }));

    const headers = Object.keys(csvData[0]);
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => headers.map(h => `"${row[h]}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alicilar_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV dosyası indirildi');
  };

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Read file and parse to show preview
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = await import('xlsx').then(XLSX => XLSX.read(data, { type: 'array' }));
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = await import('xlsx').then(XLSX => XLSX.utils.sheet_to_json(firstSheet));
        
        // Parse recipients with extensive column name support
        const parsed = jsonData.map((row, index) => {
          // Support multiple column name formats for NAME
          const name = row['İsim Soyisim'] || row['Isim Soyisim'] || row['isim soyisim'] || 
                       row['Name'] || row['name'] || row['İsim'] || row['Isim'] || 
                       row['Ad Soyad'] || row['ad soyad'] || '';
          
          // Support multiple column name formats for EMAIL
          const email = row['Email'] || row['email'] || row['E-mail'] || row['e-mail'] || 
                        row['E-Mail'] || row['EMAIL'] || row['Mail'] || row['mail'] || '';
          
          // Support multiple column name formats for GROUP
          const group = row['Grup'] || row['grup'] || row['Group'] || row['group'] || 
                        row['GROUP'] || row['GRUP'] || '';
          
          // Convert to string safely
          const emailStr = email ? String(email).trim().toLowerCase() : '';
          const nameStr = name ? String(name).trim() : '';
          const groupStr = group ? String(group).trim() : '';
          
          return {
            index: index + 1,
            name: nameStr,
            email: emailStr,
            group: groupStr
          };
        }).filter(r => r.email && r.email.includes('@')); // Only include rows with valid email
        
        if (parsed.length === 0) {
          const availableColumns = jsonData.length > 0 ? Object.keys(jsonData[0]).join(', ') : 'Yok';
          toast.error(`Excel dosyasında geçerli email bulunamadı.\n\nExcel kolonlarınız: ${availableColumns}\n\nBeklenen: İsim Soyisim, Email, Grup`);
          console.error('Available columns:', availableColumns);
          console.error('Sample row:', jsonData[0]);
          return;
        }
        
        // Show preview
        setPreviewData(parsed);
        setPreviewGroup('');
        setIsUploadOpen(false);
        setIsPreviewDialogOpen(true);
        
      } catch (error) {
        console.error('Excel parse error:', error);
        toast.error('Excel dosyası okunamadı. Lütfen format kontrol edin.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const confirmUpload = async () => {
    // Check if using manual group or Excel groups
    const useManualGroup = previewGroup.trim();
    
    // If manual group is empty, check if Excel has groups
    if (!useManualGroup) {
      const hasExcelGroups = previewData.some(r => r.group && r.group.trim());
      if (!hasExcelGroups) {
        toast.error('Lütfen bir grup adı girin veya Excel\'de grup kolonunu doldurun');
        return;
      }
    }

    try {
      // Send parsed data to backend
      const recipients = previewData.map(r => ({
        email: r.email,
        name: r.name,
        // Use manual group if provided, otherwise use Excel group
        group: useManualGroup || r.group || 'Genel'
      }));

      const response = await axios.post(`${API}/recipients/bulk`, { recipients });
      
      toast.success(`${recipients.length} alıcı başarıyla eklendi!`);
      setIsPreviewDialogOpen(false);
      setPreviewData([]);
      setPreviewGroup('');
      fetchRecipients();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Alıcılar eklenemedi';
      toast.error(errorMsg);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });

  const downloadTemplate = () => {
    // Create Excel-like CSV with proper 3 column structure
    const csvContent = 'İsim Soyisim,Email,Grup\nAhmet Yılmaz,ahmet@example.com,Kabin Memuru\nAyşe Demir,ayse@example.com,Pilot Adayları\nMehmet Kaya,mehmet@example.com,Kabin Memuru';
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'alici_sablonu.csv';
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6366f1]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="recipients-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight">Alıcılar</h1>
          <p className="text-lg text-[#64748b] mt-2">E-posta alıcı listelerinizi yönetin</p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#64748b]">Toplam Alıcı</p>
              <p className="text-3xl font-extrabold text-[#0f172a] mt-2">{totalRecipients}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#64748b]">Aktif Alıcılar</p>
              <p className="text-3xl font-extrabold text-[#10b981] mt-2">{activeRecipients}</p>
              <p className="text-xs text-[#64748b] mt-1">Mail alacaklar</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <span className="text-2xl">✅</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#64748b]">Abonelikten Çıkanlar</p>
              <p className="text-3xl font-extrabold text-[#ef4444] mt-2">{unsubscribedRecipients}</p>
              <p className="text-xs text-[#64748b] mt-1">Mail almayacaklar</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <span className="text-2xl">🚫</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-[#64748b]">Durum Filtresi:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setStatusFilter('active')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'active'
                    ? 'bg-[#6366f1] text-white'
                    : 'bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]'
                }`}
              >
                ✅ Aktif ({activeRecipients})
              </button>
              <button
                onClick={() => setStatusFilter('unsubscribed')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'unsubscribed'
                    ? 'bg-[#ef4444] text-white'
                    : 'bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]'
                }`}
              >
                🚫 Abonelikten Çıkanlar ({unsubscribedRecipients})
              </button>
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-[#0f172a] text-white'
                    : 'bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]'
                }`}
              >
                📋 Tümü ({totalRecipients})
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-[#64748b]">Grup Filtresi:</span>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="px-4 py-2 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
            >
              <option value="all">Tüm Gruplar</option>
              {uniqueGroups.map(group => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div></div>
        <div className="flex gap-3">
          <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="upload-recipients-btn"
                variant="outline"
                className="flex items-center gap-2"
              >
                <Upload size={20} />
                Upload Excel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Excel'den Alıcı Yükle</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">📋 Excel'de 3 Kolon Olmalı</h4>
                  <div className="text-xs text-blue-800 space-y-1">
                    <p className="font-semibold">Excel dosyanızda tam olarak bu 3 kolon:</p>
                    <div className="bg-white p-3 rounded mt-2 font-mono text-xs border border-blue-200">
                      <div className="grid grid-cols-3 gap-4 font-bold text-blue-900 pb-2 border-b">
                        <div>1️⃣ İsim Soyisim</div>
                        <div>2️⃣ Email</div>
                        <div>3️⃣ Grup</div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 pt-2 text-gray-700">
                        <div>Ahmet Yılmaz</div>
                        <div>ahmet@gmail.com</div>
                        <div>Kabin Memuru</div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 pt-1 text-gray-700">
                        <div>Ayşe Demir</div>
                        <div>ayse@gmail.com</div>
                        <div>Pilot Adayları</div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 pt-1 text-gray-700">
                        <div>Mehmet Kaya</div>
                        <div>mehmet@gmail.com</div>
                        <div>Kabin Memuru</div>
                      </div>
                    </div>
                    <div className="mt-3 space-y-1 text-blue-700">
                      <p className="font-semibold">✨ Nasıl Çalışır:</p>
                      <p>✅ Her satırdaki "Grup" kolonu o kişinin grubunu belirler</p>
                      <p>✅ Farklı satırlarda farklı gruplar kullanabilirsiniz</p>
                      <p>✅ Duplicate emailler otomatik atlanır</p>
                    </div>
                  </div>
                </div>

                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
                    isDragActive
                      ? 'border-[#6366f1] bg-[#6366f1]/5'
                      : 'border-[#e2e8f0] hover:border-[#6366f1] hover:bg-[#f8fafc]'
                  }`}
                >
                  <input {...getInputProps()} />
                  <Upload size={48} className="mx-auto text-[#cbd5e1] mb-4" />
                  {isDragActive ? (
                    <p className="text-[#6366f1] font-medium">Dosyayı buraya bırakın...</p>
                  ) : (
                    <div>
                      <p className="text-[#0f172a] font-medium mb-2">Excel dosyasını sürükleyin</p>
                      <p className="text-sm text-[#64748b]">veya dosya seçmek için tıklayın (.xlsx, .xls)</p>
                    </div>
                  )}
                </div>
                
                <Button
                  onClick={downloadTemplate}
                  variant="outline"
                  className="w-full"
                >
                  <Download size={16} className="mr-2" />
                  Örnek Şablon İndir (CSV)
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-recipient-btn"
                className="bg-[#6366f1] hover:bg-[#5558e3] text-white flex items-center gap-2"
              >
                <Plus size={20} />
                Add Recipient
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Recipient</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="recipient-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="name">Name (Optional)</Label>
                  <Input
                    id="name"
                    data-testid="recipient-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="group">Group (Optional)</Label>
                  <Input
                    id="group"
                    data-testid="recipient-group-input"
                    value={formData.group}
                    onChange={(e) => setFormData({ ...formData, group: e.target.value })}
                    className="mt-1"
                    placeholder="e.g., Newsletter, Customers"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" data-testid="save-recipient-btn" className="flex-1 bg-[#6366f1] hover:bg-[#5558e3] text-white">
                    Add Recipient
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Recipient Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alıcı Bilgilerini Düzenle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="edit-email">Email Adresi</Label>
              <Input
                id="edit-email"
                type="email"
                data-testid="edit-recipient-email-input"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="edit-name">İsim (Opsiyonel)</Label>
              <Input
                id="edit-name"
                data-testid="edit-recipient-name-input"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                className="mt-1"
                placeholder="Örn: Ahmet Yılmaz"
              />
            </div>
            <div>
              <Label htmlFor="edit-group">Grup</Label>
              <div className="mt-1">
                {uniqueGroups.length > 0 && (
                  <select
                    value={editFormData.group}
                    onChange={(e) => setEditFormData({ ...editFormData, group: e.target.value })}
                    className="w-full px-3 py-2 border border-[#e2e8f0] rounded-lg mb-2"
                  >
                    <option value="">Grup Yok</option>
                    {uniqueGroups.map((group) => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </select>
                )}
                <Input
                  id="edit-group"
                  data-testid="edit-recipient-group-input"
                  value={editFormData.group}
                  onChange={(e) => setEditFormData({ ...editFormData, group: e.target.value })}
                  placeholder="Veya yeni grup adı yazın"
                  className="w-full"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button 
                type="submit" 
                data-testid="update-recipient-btn" 
                className="flex-1 bg-[#6366f1] hover:bg-[#5558e3] text-white"
              >
                Güncelle
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingRecipient(null);
                }}
                className="flex-1"
              >
                İptal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Actions Toolbar */}
      {selectedRecipients.length > 0 && (
        <div className="bg-[#6366f1] text-white rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-semibold">{selectedRecipients.length} alıcı seçildi</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setIsBulkGroupDialogOpen(true)}
              variant="outline"
              className="bg-white text-[#6366f1] hover:bg-gray-100"
            >
              <FolderInput size={16} className="mr-2" />
              Grup Değiştir
            </Button>
            <Button
              onClick={handleExportCSV}
              variant="outline"
              className="bg-white text-[#6366f1] hover:bg-gray-100"
            >
              <FileDown size={16} className="mr-2" />
              CSV İndir
            </Button>
            <Button
              onClick={handleBulkDelete}
              variant="outline"
              className="bg-white text-red-600 hover:bg-red-50"
            >
              <Trash2 size={16} className="mr-2" />
              Toplu Sil
            </Button>
            <Button
              onClick={() => setSelectedRecipients([])}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              İptal
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Group Change Dialog */}
      <Dialog open={isBulkGroupDialogOpen} onOpenChange={setIsBulkGroupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Toplu Grup Değiştir</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-[#64748b]">{selectedRecipients.length} alıcının grubu değiştirilecek</p>
            <div>
              <Label htmlFor="bulk-group">Yeni Grup</Label>
              {uniqueGroups.length > 0 && (
                <select
                  value={bulkGroup}
                  onChange={(e) => setBulkGroup(e.target.value)}
                  className="w-full px-3 py-2 border border-[#e2e8f0] rounded-lg mb-2 mt-2"
                >
                  <option value="">-- Grup Seçin --</option>
                  {uniqueGroups.map((group) => (
                    <option key={group} value={group}>{group}</option>
                  ))}
                </select>
              )}
              <Input
                id="bulk-group"
                value={bulkGroup}
                onChange={(e) => setBulkGroup(e.target.value)}
                placeholder="Veya yeni grup adı yazın"
                className="mt-1"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button 
                onClick={handleBulkGroupChange}
                className="flex-1 bg-[#6366f1] hover:bg-[#5558e3] text-white"
              >
                Grup Değiştir
              </Button>
              <Button
                onClick={() => {
                  setIsBulkGroupDialogOpen(false);
                  setBulkGroup('');
                }}
                variant="outline"
                className="flex-1"
              >
                İptal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview & Confirm Upload Dialog */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">📋 Alıcı Önizleme</DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            {/* Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                  <Users size={32} className="text-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-blue-900">{previewData.length} Kişi</h3>
                  <p className="text-blue-700">Excel'den yüklendi ve hazır</p>
                </div>
              </div>
            </div>

            {/* Group Selection */}
            <div className="mb-6 p-5 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
              <Label className="text-lg font-semibold text-gray-800 mb-3 block">
                📁 Grup Ayarları
              </Label>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Tüm alıcıları tek bir gruba ekle (Opsiyonel)
                  </label>
                  <Input
                    placeholder="Boş bırakırsanız, Excel'deki grup kolonları kullanılır"
                    value={previewGroup}
                    onChange={(e) => setPreviewGroup(e.target.value)}
                    className="text-base h-11 border-2"
                  />
                </div>
                <div className="text-sm text-gray-600 bg-white p-3 rounded-lg border border-yellow-300">
                  <p className="font-semibold mb-1">💡 Nasıl Çalışır:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li><strong>Yukarıyı doldurursanız:</strong> Tüm alıcılar aynı gruba eklenir</li>
                    <li><strong>Boş bırakırsanız:</strong> Excel'deki "Grup" kolonu kullanılır</li>
                    <li><strong>Excel'de grup yoksa:</strong> "Genel" grubuna eklenir</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Preview Table */}
            <div className="border-2 border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-100 px-4 py-3 border-b-2 border-gray-200">
                <h4 className="font-semibold text-gray-800">Yüklenecek Alıcılar</h4>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">İsim</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Excel Grup</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {previewData.slice(0, 50).map((recipient) => (
                      <tr key={recipient.index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">{recipient.index}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{recipient.name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-blue-600 font-mono">{recipient.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{recipient.group || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewData.length > 50 && (
                  <div className="p-3 bg-gray-50 text-center text-sm text-gray-600">
                    ... ve {previewData.length - 50} kişi daha
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setIsPreviewDialogOpen(false);
                  setPreviewData([]);
                  setPreviewGroup('');
                  setIsUploadOpen(true);
                }}
                className="flex-1"
              >
                ← Geri Dön
              </Button>
              <Button
                onClick={confirmUpload}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-lg h-12"
              >
                ✅ {previewData.length} Kişiyi Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recipients Table */}
      {filteredRecipients.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-[#e2e8f0]">
          <Users size={64} className="mx-auto text-[#cbd5e1] mb-4" />
          <h3 className="text-xl font-bold text-[#0f172a] mb-2">No recipients yet</h3>
          <p className="text-[#64748b] mb-6">Add recipients manually or upload from Excel</p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => setIsUploadOpen(true)}
              variant="outline"
            >
              <Upload size={20} className="mr-2" />
              Upload Excel
            </Button>
            <Button
              onClick={() => setIsDialogOpen(true)}
              className="bg-[#6366f1] hover:bg-[#5558e3] text-white"
            >
              <Plus size={20} className="mr-2" />
              Add Recipient
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#e2e8f0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0f172a] w-12">
                    <button onClick={toggleSelectAll} className="p-1 hover:bg-[#e2e8f0] rounded">
                      {selectedRecipients.length === filteredRecipients.length ? (
                        <CheckSquare size={20} className="text-[#6366f1]" />
                      ) : (
                        <Square size={20} className="text-[#64748b]" />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0f172a]">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0f172a]">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0f172a]">Group</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0f172a]">Added</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-[#0f172a]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {filteredRecipients.map((recipient) => (
                  <tr key={recipient.id} className="hover:bg-[#f8fafc] transition-colors" data-testid={`recipient-row-${recipient.id}`}>
                    <td className="px-6 py-4">
                      <button 
                        onClick={() => toggleSelectRecipient(recipient.id)}
                        className="p-1 hover:bg-[#e2e8f0] rounded"
                      >
                        {selectedRecipients.includes(recipient.id) ? (
                          <CheckSquare size={20} className="text-[#6366f1]" />
                        ) : (
                          <Square size={20} className="text-[#64748b]" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-[#0f172a] font-medium">{recipient.email}</span>
                        {recipient.unsubscribed && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded-md text-xs font-bold">
                            🚫 Abonelikten Çıktı
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#64748b]">{recipient.name || '-'}</td>
                    <td className="px-6 py-4 text-sm">
                      {recipient.group ? (
                        <span className="px-2 py-1 bg-[#6366f1]/10 text-[#6366f1] rounded-md text-xs font-medium">
                          {recipient.group}
                        </span>
                      ) : (
                        <span className="text-[#64748b]">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#64748b]">
                      {new Date(recipient.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          data-testid={`edit-recipient-${recipient.id}`}
                          onClick={() => handleEdit(recipient)}
                          className="p-2 hover:bg-[#e0e7ff] text-[#6366f1] rounded-lg transition-colors"
                          title="Alıcıyı düzenle"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          data-testid={`delete-recipient-${recipient.id}`}
                          onClick={() => handleDelete(recipient.id)}
                          className="p-2 hover:bg-[#fee] text-[#ef4444] rounded-lg transition-colors"
                          title={recipient.unsubscribed ? "Abonelikten çıkmış alıcıyı sil" : "Alıcıyı sil"}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recipients;